// Story 7.5 (ADO-909) — personal favourites client (FR16).
//
// Thin typed wrapper over the Bearer-attaching `apiFetch` transport for the
// SearchAndDiscovery favourites API. Mirrors `browse.client.ts` /
// `availability.client.ts`: the SINGLE consumer seam until the
// `openapi-typescript`-generated client lands (7.1 D-6), exercised against msw
// in tests. The backend route is bare `me/favourites` (D-4); this client
// prefixes `API_PREFIX`.
//
// The wire shapes are a LOCK-STEP COPY of the module's exported
// `FavouriteProductDto` / `FavouritesListDto` (search-and-discovery
// contracts/dtos) — keep the two in sync. Every mutation (add/remove) returns
// the FULL post-state list so the store reconciles against server truth in one
// round-trip.

import { API_PREFIX, apiFetch } from './shoplink-api';
import { withEntry } from './catalogue-entry';

/** A favourited product (lock-step with `FavouriteProductDto`). Availability
 *  (Story 7.3) and price are composed client-side, as in browse/search. */
export interface FavouriteProduct {
  readonly id: string;
  readonly sku: string;
  readonly name: string;
  readonly description: string | null;
  readonly packSize: string | null;
  readonly unitOfMeasure: string | null;
  /** Story 7.4 (ADO-908): primary product image URL, or `null` → branded
   *  placeholder. Lock-step with `ProductSummary.imageUrl` (browse) — the
   *  /favourites screen renders the shared ProductCard, whose prop requires it. */
  readonly imageUrl: string | null;
  /** Picked fresh: no stock state, always orderable (Enabler E7-B, ADO-2273; MP AVL-5.4). The
   *  availability read leaves it out, so the card shows SL1.0's "Picked fresh" where the stock
   *  pill would be (Enabler E7-C, ADO-2274). Lock-step with the backend DTO. */
  readonly isPickedFresh: boolean;
  /** Account types this product is orderable for (FR18). The card greys + shows
   *  "Not on account" when this no longer contains the caller's accountType (AC4b). */
  readonly accountTypeEligible: readonly string[];
}

interface FavouritesListResponse {
  readonly favourites: readonly FavouriteProduct[];
}

export const FAVOURITES_PATH = `${API_PREFIX}/me/favourites`;

/**
 * Build the Error for a failed mutation, PRESERVING the server's user-facing
 * message when present (Story 7.5 review). A 400 from the add cap carries a
 * human message ("You can save up to 500 favourites…") in the Nest error body
 * (`{ message }`, string or string[]); surfacing it lets the store expose WHY a
 * heart keeps bouncing rather than swallowing the reason. Falls back to a
 * status-coded message when the body isn't the expected JSON shape.
 */
async function mutationError(res: Response, fallback: string): Promise<Error> {
  try {
    const body: unknown = await res.json();
    const message = (body as { message?: unknown } | null)?.message;
    if (typeof message === 'string' && message.length > 0) return new Error(message);
    if (Array.isArray(message) && message.length > 0) return new Error(message.join(', '));
  } catch {
    // Body wasn't JSON (or was empty) — fall through to the generic message.
  }
  return new Error(`${fallback} (${res.status})`);
}

/** The path with the selected ordering entry, when there is one (Enabler E7-C, ADO-2274, D-1). */
function favouritesPath(path: string, entry: string | null): string {
  return entry === null ? path : withEntry(path, entry);
}

/**
 * How long one favourites call (request and body) may take. The favourites store runs its reads
 * and writes one at a time (Enabler E7-C, ADO-2274), so a call that never answers would hold
 * every later heart; a timeout turns it into an ordinary failure the store rolls back.
 */
export const FAVOURITES_REQUEST_TIMEOUT_MS = 15_000;

/** The message a timed-out call fails with, shown beside the heart. */
export const FAVOURITES_TIMEOUT_MESSAGE = 'Favourites took too long to respond. Please try again.';

/**
 * Run `call` with a signal that aborts after `FAVOURITES_REQUEST_TIMEOUT_MS`. A plain timer, not
 * `AbortSignal.timeout`, for the older mobile Safari the scan flow also supports.
 */
async function withTimeout<T>(call: (signal: AbortSignal) => Promise<T>): Promise<T> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), FAVOURITES_REQUEST_TIMEOUT_MS);
  try {
    return await call(controller.signal);
  } catch (error) {
    if (controller.signal.aborted) throw new Error(FAVOURITES_TIMEOUT_MESSAGE);
    throw error;
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Fetch the caller's favourites (newest first). Empty is a valid empty state.
 *
 * Enabler E7-C (ADO-2274), D-1: `entry` is optional on the read and the remove. The boot
 * hydration runs before an entry exists; with one, each product is summarised from its
 * row in that entry's range.
 */
export async function listFavourites(
  entry: string | null = null,
): Promise<readonly FavouriteProduct[]> {
  return withTimeout(async (signal) => {
    const res = await apiFetch(favouritesPath(FAVOURITES_PATH, entry), { signal });
    if (!res.ok) throw new Error(`Failed to load favourites (${res.status})`);
    return ((await res.json()) as FavouritesListResponse).favourites;
  });
}

/**
 * Add `skuCode` (idempotent, AC1/AC3). Returns the full post-add list.
 *
 * The add needs the selected entry: without one the API answers 400, and it answers 404 for a
 * product the caller cannot open under that entry. Browse, search and the product page only
 * render under an entry; the favourites view can render without one (staff before picking a
 * depot), where a re-add fails and the store rolls the heart back like any failed write.
 */
export async function addFavourite(
  skuCode: string,
  entry: string | null = null,
): Promise<readonly FavouriteProduct[]> {
  return withTimeout(async (signal) => {
    const res = await apiFetch(favouritesPath(FAVOURITES_PATH, entry), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ skuCode }),
      signal,
    });
    if (!res.ok) throw await mutationError(res, 'Failed to add favourite');
    return ((await res.json()) as FavouritesListResponse).favourites;
  });
}

/** Remove `skuCode` (idempotent, AC2). Returns the full post-remove list. */
export async function removeFavourite(
  skuCode: string,
  entry: string | null = null,
): Promise<readonly FavouriteProduct[]> {
  const path = favouritesPath(`${FAVOURITES_PATH}/${encodeURIComponent(skuCode)}`, entry);
  return withTimeout(async (signal) => {
    const res = await apiFetch(path, { method: 'DELETE', signal });
    if (!res.ok) throw await mutationError(res, 'Failed to remove favourite');
    return ((await res.json()) as FavouritesListResponse).favourites;
  });
}
